import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { ChevronDown } from "lucide-react";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";

const useStyles = createUseStyles({
  button: {
    position: "fixed",
    bottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
    left: "50%",
    transform: ({ visible }) =>
      visible
        ? "translateX(-50%) translateY(0)"
        : "translateX(-50%) translateY(8px)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    borderRadius: 9999,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
    color: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    cursor: "pointer",
    outline: "none",
    zIndex: 1000,
    opacity: ({ visible }) => (visible ? 1 : 0),
    visibility: ({ visible }) => (visible ? "visible" : "hidden"),
    pointerEvents: ({ visible }) => (visible ? "auto" : "none"),
    transition:
      "opacity 300ms ease, transform 300ms ease, visibility 300ms ease, background-color 150ms ease",
    animation: ({ visible, attention }) =>
      visible && attention ? "$buttonJump 2.4s infinite ease-in-out" : "none",
    "&:hover": {
      backgroundColor: "rgba(25, 25, 25, 0.85)",
      borderColor: "rgba(255, 255, 255, 0.3)",
      animationPlayState: "paused",
    },
    "&:focus-visible": {
      boxShadow: "0 0 0 2px #4484F1, 0 4px 16px rgba(0, 0, 0, 0.35)",
    },
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none !important",
      transition: "none !important",
    },
  },
  chevronIcon: {
    display: "flex",
    alignItems: "center",
  },
  "@keyframes buttonJump": {
    "0%, 100%": {
      transform: "translateX(-50%) translateY(0)",
    },
    "20%": {
      transform: "translateX(-50%) translateY(-6px)",
    },
    "40%": {
      transform: "translateX(-50%) translateY(0)",
    },
    "60%": {
      transform: "translateX(-50%) translateY(-3px)",
    },
    "80%": {
      transform: "translateX(-50%) translateY(0)",
    },
  },
});

export const ScrollPromptButton = ({
  targetId = "scrollytell-content",
  delay = 1000,
  attentionDelay = 2500,
  threshold = 20,
}) => {
  const [visible, setVisible] = useState(false);
  const [attention, setAttention] = useState(false);
  const timerRef = useRef(null);
  const attentionTimerRef = useRef(null);
  const dismissedRef = useRef(false);
  const t = useScrollytellTranslation();
  const classes = useStyles({ visible, attention });

  useEffect(() => {
    const isAtCover = (container) => (container.scrollTop || 0) < 80;

    const clearTimers = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (attentionTimerRef.current) {
        clearTimeout(attentionTimerRef.current);
        attentionTimerRef.current = null;
      }
    };

    const checkAndShow = () => {
      if (dismissedRef.current) return;
      const container = document.getElementById(targetId);
      if (!container) return;

      if (!isAtCover(container)) {
        setVisible(false);
        setAttention(false);
        return;
      }

      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - threshold;

      if (!isAtBottom) {
        setVisible(true);
        if (attentionTimerRef.current) {
          clearTimeout(attentionTimerRef.current);
        }
        attentionTimerRef.current = setTimeout(() => {
          if (!dismissedRef.current) {
            setAttention(true);
          }
        }, attentionDelay);
      }
    };

    const handleScroll = () => {
      setVisible(false);
      setAttention(false);
      clearTimers();

      const container = document.getElementById(targetId);
      if (!container || dismissedRef.current) return;

      // Only queue prompt if still at the cover
      if (isAtCover(container)) {
        timerRef.current = setTimeout(checkAndShow, delay);
      }
    };

    const container = document.getElementById(targetId);
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
      if (isAtCover(container)) {
        timerRef.current = setTimeout(checkAndShow, delay);
      }
    }

    return () => {
      clearTimers();
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [targetId, delay, attentionDelay, threshold]);

  const handleClick = () => {
    dismissedRef.current = true;
    setVisible(false);
    setAttention(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (attentionTimerRef.current) {
      clearTimeout(attentionTimerRef.current);
      attentionTimerRef.current = null;
    }

    const container = document.getElementById(targetId);
    if (!container || typeof container.scrollBy !== "function") return;

    const viewportHeight = container.clientHeight || window.innerHeight;
    const containerRect =
      typeof container.getBoundingClientRect === "function"
        ? container.getBoundingClientRect()
        : { top: 0, bottom: viewportHeight };

    // Query candidate milestone elements (sections and sticky scroller steps)
    const elements = container.querySelectorAll(
      'section, [class*="stickyWrapper"]'
    );

    const minDistance = 40; // Skip elements we are already aligned with
    let nextMilestoneDistance = null;

    elements.forEach((el) => {
      if (typeof el.getBoundingClientRect === "function") {
        const rect = el.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        if (relativeTop > minDistance) {
          if (
            nextMilestoneDistance === null ||
            relativeTop < nextMilestoneDistance
          ) {
            nextMilestoneDistance = relativeTop;
          }
        }
      }
    });

    let scrollDistance;
    // If the next milestone is within about 1 screen height (up to 1.15 viewports),
    // snap cleanly to it
    if (
      nextMilestoneDistance !== null &&
      nextMilestoneDistance <= viewportHeight * 1.15
    ) {
      scrollDistance = Math.round(nextMilestoneDistance);
    } else {
      // Otherwise, advance by 80% of viewport to maintain reading continuity in tall sections
      scrollDistance = Math.round(viewportHeight * 0.8);
    }

    container.scrollBy({
      top: scrollDistance,
      behavior: "smooth",
    });
  };

  const label = t("ScrollPromptText", "Scroll videre");

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handleClick}
      aria-label={label}
      data-visible={visible}
      data-attention={attention}
    >
      <span>{label}</span>
      <span className={classes.chevronIcon}>
        <ChevronDown size={16} aria-hidden="true" />
      </span>
    </button>
  );
};
